import React, { useMemo, useState, useCallback } from 'react';
import { FilterSelect } from '../FilterSelect';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Treemap, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
} from 'recharts';
import { getClassificationInfo, formatISODate } from '../badges';
import {
  RasffNotification, FilterKey, Filters,
  TIME_FRAMES, CLASS_COLORS, TOOLTIP_CONTENT_STYLE,
  fetchRasff, getVal, applyFilters, countField, classifyType,
  buildOriginCountryData, buildProductHazardBubbles, getCategoryColor,
} from './rasffUtils';
import { DateRangePicker } from '../DateRangePicker';
import {
  useUrlState, UrlStateConfig,
  stringCodec, nullableStringCodec, daysCodec,
} from '../../hooks/useUrlState';

/** URL-synced state for the homepage RASFF dashboard. Keys here become URL
 *  query params; defaults are omitted. Names match RasffDeepDivePage so the
 *  same deep-link works on both surfaces. */
type UrlState = {
  days: number;
  from: string;
  to: string;
  category: string | null;
  hazardCategory: string | null;
  hazard: string | null;
  class: string | null;
  risk: string | null;
  origin: string | null;
  notifying: string | null;
  dist: string | null;
  productType: string | null;
};

/** Module-level constant — stable identity across renders. */
const URL_CONFIG: UrlStateConfig<UrlState> = {
  days:           daysCodec(30),
  from:           stringCodec(''),
  to:             stringCodec(''),
  category:       nullableStringCodec(),
  hazardCategory: nullableStringCodec(),
  hazard:         nullableStringCodec(),
  class:          nullableStringCodec(),
  risk:           nullableStringCodec(),
  origin:         nullableStringCodec(),
  notifying:      nullableStringCodec(),
  dist:           nullableStringCodec(),
  productType:    nullableStringCodec(),
};

/** Map the internal FilterKey (from rasffUtils) to its URL param name. */
const FILTER_KEY_TO_URL: Record<FilterKey, keyof UrlState> = {
  category:         'category',
  hazardCategory:   'hazardCategory',
  hazard:           'hazard',
  classification:   'class',
  riskDecision:     'risk',
  originCountry:    'origin',
  notifyingCountry: 'notifying',
  distCountry:      'dist',
  productType:      'productType',
};

/* ── Shared tooltip box (consistent style across all charts) ── */
function TooltipBox({ children }: { children: React.ReactNode }) {
  return <div style={TOOLTIP_CONTENT_STYLE}>{children}</div>;
}

/* ── Treemap cell renderer ── */
function TreemapCell(props: any) {
  const { x, y, width, height, name, value, activeCategory } = props;
  if (!width || !height || width < 4 || height < 4) return null;
  const isActive = !activeCategory || activeCategory === name;
  const fill = getCategoryColor(name || '');
  // How many chars fit at font-size 11 in the available width (approx 6.5px per char)
  const maxChars = Math.max(3, Math.floor(width / 6.5));
  const label = name && name.length > maxChars ? name.slice(0, maxChars - 1) + '…' : (name || '');
  return (
    <g style={{ cursor: 'pointer' }}>
      <rect
        x={x} y={y} width={width} height={height}
        fill={fill} opacity={isActive ? 1 : 0.25}
        stroke="#FEFDF8" strokeWidth={2} rx={2}
      >
        {/* Native tooltip for truncated/unlabelled tiles */}
        <title>{`${name || ''} — ${value} notifications`}</title>
      </rect>
      {width > 50 && height > 26 && (
        <text
          x={x + 5} y={y + 14}
          fill="#fff" fontSize={11} fontWeight={600}
          fontFamily="Plus Jakarta Sans, system-ui, sans-serif"
          style={{ pointerEvents: 'none' }}
        >
          <tspan x={x + 5} dy={0}>{label}</tspan>
          {height > 44 && (
            <tspan x={x + 5} dy={14} fill="rgba(255,255,255,0.75)" fontSize={10} fontWeight={400}>{value}</tspan>
          )}
        </text>
      )}
    </g>
  );
}

/* ── Product × Hazard Heatmap ── */
function ProductHazardHeatmap({
  bubbles, productNames, hazardNames, onCellClick,
}: {
  bubbles: { x: number; y: number; z: number; productName: string; hazardName: string }[];
  productNames: string[];
  hazardNames: string[];
  onCellClick: (product: string, hazard: string) => void;
}) {
  const maxZ = Math.max(...bubbles.map(b => b.z), 1);
  const lookup = new Map(bubbles.map(b => [`${b.x},${b.y}`, b]));
  const [tip, setTip] = useState<{ x: number; y: number; product: string; hazard: string; count: number } | null>(null);

  const cellColor = (z: number) => {
    if (z === 0) return 'rgba(27,67,50,0.06)';
    const t = z / maxZ;
    return `rgba(27,67,50,${0.15 + 0.81 * t})`;
  };
  return (
    <div className="rasff-dash__heatmap">
      <div className="rasff-dash__heatmap-grid"
        style={{ gridTemplateColumns: `110px repeat(${productNames.length}, 1fr)` }}
      >
        <div />
        {productNames.map((p, pi) => (
          <div key={pi} className="rasff-dash__heatmap-col-label" title={p}>
            {p}
          </div>
        ))}
        {hazardNames.map((h, hi) => (
          <React.Fragment key={hi}>
            <div className="rasff-dash__heatmap-row-label" title={h}>
              {h}
            </div>
            {productNames.map((p, pi) => {
              const cell = lookup.get(`${pi},${hi}`);
              const z = cell?.z || 0;
              const t = z / maxZ;
              return (
                <div
                  key={pi}
                  className="rasff-dash__heatmap-cell"
                  style={{ background: cellColor(z), cursor: z > 0 ? 'pointer' : 'default' }}
                  onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY, product: p, hazard: h, count: z })}
                  onMouseMove={e => setTip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                  onMouseLeave={() => setTip(null)}
                  onClick={() => z > 0 && onCellClick(p, h)}
                >
                  {z > 0 && (
                    <span style={{ color: t > 0.45 ? '#fff' : 'var(--color-text-secondary)', fontWeight: 700, fontSize: 11 }}>
                      {z}
                    </span>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      {tip && (
        <div style={{
          position: 'fixed',
          left: tip.x + 14,
          top: tip.y + 14,
          zIndex: 300,
          pointerEvents: 'none',
          ...TOOLTIP_CONTENT_STYLE,
        }}>
          <div style={{ fontWeight: 600 }}>{tip.product}</div>
          <div style={{ color: 'var(--color-text-secondary)', marginTop: 2 }}>{tip.hazard}</div>
          <div style={{ marginTop: 4, fontWeight: 600 }}>{tip.count > 0 ? `${tip.count} notifications` : 'No notifications'}</div>
        </div>
      )}
    </div>
  );
}

/* ── Component ── */
export default function RasffDashboard() {
  /* URL is the source of truth so every filtered view is bookmark/shareable.
     Param names match RasffDeepDivePage, so `?hazard=X&days=30` works on both. */
  const { state, patch } = useUrlState(URL_CONFIG);
  const days     = state.days;
  const dateFrom = state.from;
  const dateTo   = state.to;

  const today = new Date().toISOString().split('T')[0];
  const { data: notifications, isLoading, isError } = useQuery(
    ['rasff-dashboard', days],
    () => fetchRasff(days),
  );

  /* Previous-period data for KPI deltas (skip for "All") */
  const { data: notifications2x } = useQuery(
    ['rasff-dashboard-prev', days],
    () => fetchRasff(days * 2),
    { enabled: days > 0 },
  );

  const filters: Filters = useMemo(() => ({
    category:         state.category,
    hazardCategory:   state.hazardCategory,
    hazard:           state.hazard,
    classification:   state.class,
    riskDecision:     state.risk,
    originCountry:    state.origin,
    notifyingCountry: state.notifying,
    distCountry:      state.dist,
    productType:      state.productType,
  }), [
    state.category, state.hazardCategory, state.hazard, state.class,
    state.risk, state.origin, state.notifying, state.dist, state.productType,
  ]);

  /* `tableExpanded` / `filtersOpen` are pure UI toggles — intentionally NOT in the URL. */
  const [tableExpanded, setTableExpanded] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const setFilter = useCallback((key: FilterKey, value: string | null) => {
    const urlKey = FILTER_KEY_TO_URL[key];
    patch({ [urlKey]: value } as Partial<UrlState>);
  }, [patch]);

  const clearFilters = useCallback(() => {
    patch({
      category: null, hazardCategory: null, hazard: null,
      class: null, risk: null, origin: null,
      notifying: null, dist: null, productType: null,
    });
  }, [patch]);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const activeFilters = activeFilterCount > 0;

  /* ── Cascading filter options ── */
  const optCategory        = useMemo(() => countField(applyFilters(notifications || [], filters, 'category'), 'category'), [notifications, filters]);
  const optHazardCategory  = useMemo(() => countField(applyFilters(notifications || [], filters, 'hazardCategory'), 'hazardCategory'), [notifications, filters]);
  const optHazard          = useMemo(() => countField(applyFilters(notifications || [], filters, 'hazard'), 'hazard'), [notifications, filters]);
  const optClassification  = useMemo(() => countField(applyFilters(notifications || [], filters, 'classification'), 'classification'), [notifications, filters]);
  const optRiskDecision    = useMemo(() => countField(applyFilters(notifications || [], filters, 'riskDecision'), 'riskDecision'), [notifications, filters]);
  const optOriginCountry   = useMemo(() => countField(applyFilters(notifications || [], filters, 'originCountry'), 'originCountry'), [notifications, filters]);
  const optNotifyingCountry = useMemo(() => countField(applyFilters(notifications || [], filters, 'notifyingCountry'), 'notifyingCountry'), [notifications, filters]);
  const optDistCountry     = useMemo(() => countField(applyFilters(notifications || [], filters, 'distCountry'), 'distCountry'), [notifications, filters]);
  const optProductType     = useMemo(() => countField(applyFilters(notifications || [], filters, 'productType'), 'productType'), [notifications, filters]);

  /* ── Filtered result (+ optional dateFrom / dateTo cap) ── */
  const filtered = useMemo(() => {
    let result = applyFilters(notifications || [], filters);
    if (dateFrom) {
      const start = new Date(dateFrom); start.setHours(0, 0, 0, 0);
      result = result.filter(n => !n.ec_validation_date || new Date(n.ec_validation_date) >= start);
    }
    if (dateTo) {
      const end = new Date(dateTo); end.setHours(23, 59, 59, 999);
      result = result.filter(n => !n.ec_validation_date || new Date(n.ec_validation_date) <= end);
    }
    return result;
  }, [notifications, filters, dateFrom, dateTo]);

  /* ── KPI counts ── */
  const kpiAlerts  = useMemo(() => filtered.filter(n => classifyType(n.classification) === 'Alert').length, [filtered]);
  const kpiBorder  = useMemo(() => filtered.filter(n => classifyType(n.classification) === 'Border Rejection').length, [filtered]);
  const kpiHazards = useMemo(() => new Set(filtered.map(n => n.dim_hazards?.hazardname).filter(Boolean)).size, [filtered]);
  const kpiOrigins = useMemo(() => new Set(filtered.flatMap(n => n.origin_countries ? n.origin_countries.split(', ') : [])).size, [filtered]);

  /* ── KPI deltas vs prior equivalent window ── */
  const kpiDelta = useMemo(() => {
    if (!notifications2x || days === 0) return null;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const prevRaw    = notifications2x.filter(n =>
      n.ec_validation_date && new Date(n.ec_validation_date) < cutoff,
    );
    const prev       = applyFilters(prevRaw, filters);
    const prevAlerts  = prev.filter(n => classifyType(n.classification) === 'Alert').length;
    const prevBorder  = prev.filter(n => classifyType(n.classification) === 'Border Rejection').length;
    const prevHazards = new Set(prev.map(n => n.dim_hazards?.hazardname).filter(Boolean)).size;
    const prevOrigins = new Set(prev.flatMap(n => n.origin_countries ? n.origin_countries.split(', ') : [])).size;
    return {
      alerts:  kpiAlerts  - prevAlerts,
      border:  kpiBorder  - prevBorder,
      hazards: kpiHazards - prevHazards,
      origins: kpiOrigins - prevOrigins,
    };
  }, [notifications2x, days, filters, kpiAlerts, kpiBorder, kpiHazards, kpiOrigins]);

  /* ── Chart data ── */
  const drillHazard = !!filters.hazardCategory;
  const treemapData       = useMemo(() => countField(filtered, 'category').map(d => ({ name: d.name, size: d.value })), [filtered]);
  const originCountryData = useMemo(() => buildOriginCountryData(filtered), [filtered]);
  const { bubbles: phBubbles, productNames, hazardNames } = useMemo(
    () => buildProductHazardBubbles(filtered, 8, drillHazard ? 10 : 6, drillHazard),
    [filtered, drillHazard],
  );

  if (isLoading) return (
    <div className="dash-card dash-card--full dash-card--hero">
      <div className="dash-card__body" style={{ padding: '2rem', textAlign: 'center' }}>Loading RASFF data…</div>
    </div>
  );
  if (isError) return (
    <div className="dash-card dash-card--full dash-card--hero">
      <div className="dash-card__body dash-card__msg--error" style={{ padding: '2rem', textAlign: 'center' }}>Failed to load RASFF data</div>
    </div>
  );

  return (
    <div className="dash-card dash-card--full dash-card--hero">

      {/* ── Header ── */}
      <div className="dash-card__header">
        <h3>RASFF Notifications</h3>
        <div className="rasff-dash__header-right">
          <div className="rasff-dash__timeframe">
            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              days={days}
              today={today}
              presets={TIME_FRAMES}
              onPreset={d => patch({ days: d, from: '', to: '' })}
              onRange={(from, to, apiDays) => patch({ from, to, days: apiDays })}
            />
          </div>
          <span className="rasff-dash__count">{filtered.length}</span>
          <span className="rasff-dash__count-label">notifications</span>
          <Link to="/rasff" className="rasff-dash__deep-dive-link">
            Deep Dive →
          </Link>
        </div>
      </div>

      {/* ── Filters ── */}
      {/* Mobile-only disclosure — hidden on desktop via CSS */}
      <button
        className="rasff-dash__filters-toggle"
        onClick={() => setFiltersOpen(o => !o)}
        aria-expanded={filtersOpen}
      >
        <span>Filters{activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}</span>
        <span className={`rasff-dash__toggle-icon ${filtersOpen ? 'rasff-dash__toggle-icon--open' : ''}`} />
      </button>
      <div className={`rasff-dash__filters${filtersOpen ? ' rasff-dash__filters--open' : ''}`}>
        <FilterSelect value={filters.category || ''} onChange={v => setFilter('category', v || null)} placeholder="All categories"
          options={optCategory.map(c => ({ value: c.name, label: `${c.name} (${c.value})` }))} />
        <FilterSelect value={filters.hazardCategory || ''} onChange={v => setFilter('hazardCategory', v || null)} placeholder="All hazard categories"
          options={optHazardCategory.map(h => ({ value: h.name, label: `${h.name} (${h.value})` }))} />
        <FilterSelect value={filters.hazard || ''} onChange={v => setFilter('hazard', v || null)} placeholder="All hazards"
          options={optHazard.map(h => ({ value: h.name, label: `${h.name} (${h.value})` }))} />
        <FilterSelect value={filters.classification || ''} onChange={v => setFilter('classification', v || null)} placeholder="All classifications"
          options={optClassification.map(c => ({ value: c.name, label: `${getClassificationInfo(c.name).label} (${c.value})` }))} />
        <FilterSelect value={filters.riskDecision || ''} onChange={v => setFilter('riskDecision', v || null)} placeholder="All risk decisions"
          options={optRiskDecision.map(r => ({ value: r.name, label: `${r.name} (${r.value})` }))} />
        <FilterSelect value={filters.originCountry || ''} onChange={v => setFilter('originCountry', v || null)} placeholder="All origin countries"
          options={optOriginCountry.map(c => ({ value: c.name, label: `${c.name} (${c.value})` }))} />
        <FilterSelect value={filters.notifyingCountry || ''} onChange={v => setFilter('notifyingCountry', v || null)} placeholder="All notifying countries"
          options={optNotifyingCountry.map(c => ({ value: c.name, label: `${c.name} (${c.value})` }))} />
        <FilterSelect value={filters.distCountry || ''} onChange={v => setFilter('distCountry', v || null)} placeholder="All distribution countries"
          options={optDistCountry.map(c => ({ value: c.name, label: `${c.name} (${c.value})` }))} />
        <FilterSelect value={filters.productType || ''} onChange={v => setFilter('productType', v || null)} placeholder="All product types"
          options={optProductType.map(p => ({ value: p.name, label: `${p.name} (${p.value})` }))} />
        {activeFilters && (
          <button className="rasff-dash__clear" onClick={clearFilters}>Clear filters</button>
        )}
      </div>

      {/* ── KPI Row ── */}
      <div className="rasff-dash__kpi-row">
        <div className="rasff-dash__kpi-tile rasff-dash__kpi-tile--alert">
          <span className="rasff-dash__kpi-value">{kpiAlerts}</span>
          <span className="rasff-dash__kpi-label">Alerts</span>
          {kpiDelta != null && (
            <span className={`rasff-dash__kpi-delta ${kpiDelta.alerts > 0 ? 'rasff-dash__kpi-delta--worse' : kpiDelta.alerts < 0 ? 'rasff-dash__kpi-delta--better' : 'rasff-dash__kpi-delta--zero'}`}>
              {kpiDelta.alerts > 0 ? '+' : ''}{kpiDelta.alerts === 0 ? '±0' : kpiDelta.alerts} vs prev
            </span>
          )}
        </div>
        <div className="rasff-dash__kpi-tile rasff-dash__kpi-tile--border">
          <span className="rasff-dash__kpi-value">{kpiBorder}</span>
          <span className="rasff-dash__kpi-label">Border Rejections</span>
          {kpiDelta != null && (
            <span className={`rasff-dash__kpi-delta ${kpiDelta.border > 0 ? 'rasff-dash__kpi-delta--worse' : kpiDelta.border < 0 ? 'rasff-dash__kpi-delta--better' : 'rasff-dash__kpi-delta--zero'}`}>
              {kpiDelta.border > 0 ? '+' : ''}{kpiDelta.border === 0 ? '±0' : kpiDelta.border} vs prev
            </span>
          )}
        </div>
        <div className="rasff-dash__kpi-tile">
          <span className="rasff-dash__kpi-value">{kpiHazards}</span>
          <span className="rasff-dash__kpi-label">Unique Hazards</span>
          {kpiDelta != null && (
            <span className={`rasff-dash__kpi-delta ${kpiDelta.hazards > 0 ? 'rasff-dash__kpi-delta--up' : kpiDelta.hazards < 0 ? 'rasff-dash__kpi-delta--down' : 'rasff-dash__kpi-delta--zero'}`}>
              {kpiDelta.hazards > 0 ? '+' : ''}{kpiDelta.hazards === 0 ? '±0' : kpiDelta.hazards} vs prev
            </span>
          )}
        </div>
        <div className="rasff-dash__kpi-tile">
          <span className="rasff-dash__kpi-value">{kpiOrigins}</span>
          <span className="rasff-dash__kpi-label">Origin Countries</span>
          {kpiDelta != null && (
            <span className={`rasff-dash__kpi-delta ${kpiDelta.origins > 0 ? 'rasff-dash__kpi-delta--up' : kpiDelta.origins < 0 ? 'rasff-dash__kpi-delta--down' : 'rasff-dash__kpi-delta--zero'}`}>
              {kpiDelta.origins > 0 ? '+' : ''}{kpiDelta.origins === 0 ? '±0' : kpiDelta.origins} vs prev
            </span>
          )}
        </div>
      </div>

      {/* ── Row 1: Treemap (full width) ── */}
      <div className="rasff-dash__charts rasff-dash__charts--full">
        <div className="rasff-dash__chart-box">
          <h4>By Product Category</h4>
          <ResponsiveContainer width="100%" height={280}>
            <Treemap
              data={treemapData}
              dataKey="size"
              aspectRatio={4 / 3}
              isAnimationActive={false}
              content={(props: any) => (
                <TreemapCell {...props} activeCategory={filters.category} />
              )}
              onClick={(entry: any) =>
                setFilter('category', filters.category === entry.name ? null : entry.name)
              }
            >
              <Tooltip
                content={({ payload }) => {
                  if (!payload?.length) return null;
                  const item = payload[0]?.payload;
                  if (!item?.name) return null;
                  return (
                    <TooltipBox>
                      <div style={{ fontWeight: 600 }}>{item.name}</div>
                      <div style={{ color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        {item.size} notifications
                      </div>
                    </TooltipBox>
                  );
                }}
              />
            </Treemap>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Row 2: Origin Countries + Product×Hazard Bubble ── */}
      <div className="rasff-dash__charts rasff-dash__charts--row2">
        <div className="rasff-dash__chart-box">
          <h4>Top Origin Countries</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={originCountryData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} fontSize={11} tick={{ fill: 'var(--color-text-secondary)' }} />
              <YAxis
                type="category" dataKey="country" width={80} fontSize={11}
                tick={{ fill: 'var(--color-text-secondary)' }} tickLine={false}
              />
              <Tooltip
                content={({ label, payload }) => {
                  if (!payload?.length) return null;
                  return (
                    <TooltipBox>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      {payload.map((p: any) => p.value > 0 && (
                        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.fill, display: 'inline-block', flexShrink: 0 }} />
                          <span style={{ color: 'var(--color-text-secondary)' }}>{p.dataKey}:</span>
                          <span style={{ fontWeight: 600 }}>{p.value}</span>
                        </div>
                      ))}
                    </TooltipBox>
                  );
                }}
              />
              {(Object.keys(CLASS_COLORS) as string[]).map(cls => (
                <Bar
                  key={cls} dataKey={cls} stackId="a"
                  fill={CLASS_COLORS[cls]}
                  onClick={(entry: any) =>
                    setFilter('originCountry', filters.originCountry === entry.country ? null : entry.country)
                  }
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="rasff-dash__cls-legend">
            {Object.entries(CLASS_COLORS).map(([cls, color]) => (
              <span key={cls} className="rasff-dash__legend-item">
                <span className="rasff-dash__legend-dot" style={{ background: color }} />
                {cls}
              </span>
            ))}
          </div>
        </div>

        <div className="rasff-dash__chart-box">
          <h4>Product × Hazard Co-occurrence{drillHazard && <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--color-text-secondary)', marginLeft: 8 }}>— specific hazards</span>}</h4>
          {phBubbles.length > 0 ? (
            <ProductHazardHeatmap
              bubbles={phBubbles}
              productNames={productNames}
              hazardNames={hazardNames}
              onCellClick={(product, hazard) => {
                setFilter('category', filters.category === product ? null : product);
                setFilter('hazardCategory', filters.hazardCategory === hazard ? null : hazard);
              }}
            />
          ) : (
            <div className="dash-card__msg">Not enough data for this view</div>
          )}
        </div>
      </div>

      {/* ── Notifications table (quick view) ── */}
      <div className="rasff-dash__table-section">
        {/* Button and link are siblings — an <a> inside a <button> is invalid HTML
            and unreachable for screen readers. */}
        <div className={`rasff-dash__table-bar ${tableExpanded ? 'rasff-dash__table-bar--open' : ''}`}>
          <button
            className="rasff-dash__table-toggle"
            onClick={() => setTableExpanded(!tableExpanded)}
            aria-expanded={tableExpanded}
          >
            <span className={`rasff-dash__toggle-icon ${tableExpanded ? 'rasff-dash__toggle-icon--open' : ''}`} />
            <span>{tableExpanded ? 'Hide' : 'Show'} notifications ({filtered.length})</span>
          </button>
          {!tableExpanded && (
            <Link to="/rasff" className="rasff-dash__deep-dive-link rasff-dash__deep-dive-link--inline">
              Full table with search →
            </Link>
          )}
        </div>

        {tableExpanded && (
          <div className="rasff-dash__table-wrap">
            <table className="rasff-dash__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Reference</th>
                  <th>Subject</th>
                  <th>Country</th>
                  <th>Product Category</th>
                  <th>Hazard Category</th>
                  <th>Hazard</th>
                  <th>Classification</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 50).map(n => {
                  const cls = getClassificationInfo(n.classification);
                  return (
                    <tr key={n.rasff_id}>
                      <td>{n.ec_validation_date ? formatISODate(n.ec_validation_date) : '—'}</td>
                      <td className="rasff-dash__ref">
                        <a href={`https://webgate.ec.europa.eu/rasff-window/screen/notification/${n.notif_id}`}
                          target="_blank" rel="noopener noreferrer">{n.reference}</a>
                      </td>
                      <td className="rasff-dash__subject">{n.subject}</td>
                      <td>{n.notifying_country || '—'}</td>
                      <td>{n.product_category || '—'}</td>
                      <td>{n.dim_hazards?.hazardscategoryname || '—'}</td>
                      <td>{n.dim_hazards?.hazardname || '—'}</td>
                      <td><span className={`rasff-badge ${cls.className}`}>{cls.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length > 50 && (
              <div className="rasff-dash__table-more">
                Showing 50 of {filtered.length} — <Link to="/rasff" style={{ color: 'var(--color-primary)' }}>View all in Deep Dive →</Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
